import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Check, AlertCircle, X, Eye, MousePointerClick } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  } catch { return "—"; }
}

export function useNotificationCount() {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setCount(data.count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000); // poll once a minute
    return () => clearInterval(id);
  }, [refresh]);

  return { count, refresh };
}

export default function NotificationDropdown({ onClose, onChange }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (id, e) => {
    e?.stopPropagation();
    setItems((prev) => prev.filter((it) => it.id !== id));
    try {
      await api.post("/notifications/dismiss", { notification_id: id });
      onChange?.();
    } catch { toast.error("Failed to dismiss"); load(); }
  };

  const dismissAll = async () => {
    try {
      await api.post("/notifications/dismiss-all");
      setItems([]);
      onChange?.();
      toast.success("All cleared");
    } catch { toast.error("Failed"); }
  };

  const openMove = (it) => {
    onClose?.();
    if (it.linked_type === "lead" && it.linked_id)       navigate(`/leads/${it.linked_id}`);
    else if (it.linked_type === "deal" && it.linked_id)  navigate(`/deals/${it.linked_id}`);
    else if (it.linked_type === "contact" && it.linked_id) navigate(`/contacts/${it.linked_id}`);
    else if (it.linked_type === "company" && it.linked_id) navigate(`/companies/${it.linked_id}`);
    else if (it.kind === "email_opened" || it.kind === "email_clicked") navigate("/moves");
    else navigate("/moves");
  };

  const completeMove = async (moveId, notifId, e) => {
    e?.stopPropagation();
    try {
      await api.patch(`/moves/${moveId}`, { status: "done" });
      setItems((prev) => prev.filter((it) => it.id !== notifId));
      onChange?.();
      toast.success("Move completed");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="notifications-pop" data-testid="notifications-popover" onMouseDown={(e) => e.stopPropagation()}>
      <div className="notifications-header">
        <div>
          <div className="notifications-title">Notifications</div>
          <div className="notifications-sub">{loading ? "Loading…" : `${items.length} unread`}</div>
        </div>
        {items.length > 0 && (
          <button
            type="button"
            className="notifications-clear"
            onClick={dismissAll}
            data-testid="notifications-clear-all"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="notifications-body">
        {loading ? (
          <div className="notifications-empty">
            <div className="notifications-empty-sub">Loading…</div>
          </div>
        ) : items.length === 0 ? (
          <div className="notifications-empty">
            <div className="notifications-empty-title">All caught up</div>
            <div className="notifications-empty-sub">Nothing new right now.</div>
          </div>
        ) : (
          items.map((it) => {
            if (it.kind === "email_opened") {
              return (
                <div
                  key={it.id}
                  className="notification-row"
                  data-testid={`notification-${it.id}`}
                  onClick={() => openMove(it)}
                >
                  <div className="notification-icon" style={{
                    background: "rgba(52,168,83,0.14)",
                    color: "var(--pg-accent-deep, #34a853)",
                  }}>
                    <Eye />
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {it.recipient || "Recipient"} opened your email
                      {it.open_count > 1 ? ` (${it.open_count}×)` : ""}
                    </div>
                    <div className="notification-meta">
                      &ldquo;{(it.subject || "").slice(0, 60)}&rdquo; · {timeAgo(it.opened_at)}
                    </div>
                    <div className="notification-actions">
                      <button
                        type="button"
                        className="notification-action"
                        onClick={(e) => dismiss(it.id, e)}
                        data-testid={`notification-dismiss-${it.id}`}
                      >
                        <X /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
            if (it.kind === "email_clicked") {
              return (
                <div
                  key={it.id}
                  className="notification-row"
                  data-testid={`notification-${it.id}`}
                  onClick={() => openMove(it)}
                >
                  <div className="notification-icon" style={{
                    background: "rgba(91,79,232,0.14)",
                    color: "var(--pg-purple, #5B4FE8)",
                  }}>
                    <MousePointerClick />
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {it.recipient || "Recipient"} clicked a link in your email
                      {it.click_count > 1 ? ` (${it.click_count}×)` : ""}
                    </div>
                    <div className="notification-meta">
                      &ldquo;{(it.subject || "").slice(0, 60)}&rdquo; · {timeAgo(it.clicked_at)}
                    </div>
                    <div className="notification-actions">
                      <button
                        type="button"
                        className="notification-action"
                        onClick={(e) => dismiss(it.id, e)}
                        data-testid={`notification-dismiss-${it.id}`}
                      >
                        <X /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div
                key={it.id}
                className="notification-row"
                data-testid={`notification-${it.id}`}
                onClick={() => openMove(it)}
              >
                <div className="notification-icon notification-icon-red">
                  <AlertCircle />
                </div>
                <div className="notification-content">
                  <div className="notification-title">Move overdue: {it.title}</div>
                  <div className="notification-meta">
                    was due {(it.due_date || "").slice(0, 10)} · {timeAgo(it.due_date)}
                    {it.linked_type && ` · on ${it.linked_type}`}
                  </div>
                  <div className="notification-actions">
                    <button
                      type="button"
                      className="notification-action"
                      onClick={(e) => completeMove(it.move_id, it.id, e)}
                      data-testid={`notification-complete-${it.id}`}
                    >
                      <Check /> Mark done
                    </button>
                    <button
                      type="button"
                      className="notification-action"
                      onClick={(e) => dismiss(it.id, e)}
                      data-testid={`notification-dismiss-${it.id}`}
                    >
                      <X /> Dismiss
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
